import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { listingsService } from "../services/listingsService";
import ListingCard from "../components/ListingCard";
import PaymentApproval from "../components/PaymentApproval";

export default function MyListings() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState('listings');

  useEffect(() => {
    const loadUserListings = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      setLoading(true);
      setError("");
      try {
        const data = await listingsService.getUserListings(user.id);
        setListings(data);
      } catch (err) {
        console.error('Failed to load user listings:', err);
        setError("Failed to load your listings");
      } finally {
        setLoading(false);
      }
    };
    
    loadUserListings();
  }, [user, navigate]);

  const handleDeleteListing = async (listingId) => {
    if (!confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await listingsService.deleteListing(listingId);
      setListings(listings.filter(listing => listing.id !== listingId));
    } catch (err) {
      console.error('Failed to delete listing:', err);
      setError("Failed to delete listing");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'sold': return 'bg-blue-100 text-blue-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Debug logging
  console.log('MyListings render:', { loading, error, listings: listings.length, activeTab, user: !!user });

  // Early return if no user
  if (!user) {
    return (
      <div className="container-center mx-auto">
        <div className="text-center py-12">
          <p className="text-slate-600">Please log in to view your listings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-center mx-auto">
        <div className="mb-8 text-center">
          <div className="h-8 bg-slate-200 rounded-lg loading-skeleton mb-2"></div>
          <div className="h-4 bg-slate-200 rounded-lg loading-skeleton w-2/3 mx-auto"></div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="h-48 bg-slate-200 loading-skeleton"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-slate-200 rounded loading-skeleton"></div>
                <div className="h-3 bg-slate-200 rounded loading-skeleton w-2/3"></div>
                <div className="h-6 bg-slate-200 rounded loading-skeleton w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-center mx-auto">
      {/* Page Header */}
      <div className="mb-8 text-center">
        <h1 className="text-responsive-xl font-bold text-slate-900 mb-2">
          {t('page.myListings')}
        </h1>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('listings')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'listings'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {t('page.myListings')}
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'payments'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {t('payment.approvals', 'Payment Approvals')}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-6">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xs">!</span>
            </div>
            <div className="text-sm text-red-800">{error}</div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'listings' && (
        <div>
          {/* Create New Listing Button */}
          <div className="mb-6">
            <button 
              className="btn-primary"
              onClick={() => navigate('/create')}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create New Listing
              </span>
            </button>
          </div>

      {/* Listings Grid */}
      {listings.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
            <span className="text-4xl">📦</span>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No listings yet</h3>
          <p className="text-slate-600 mb-6">Start selling your baby items!</p>
          <button 
            className="btn-primary"
            onClick={() => navigate('/create')}
          >
            Create Your First Listing
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {listings.map((listing) => (
            <div key={listing.id} className="relative compact-listing-card">
              {/* Status Badge */}
              <div className="absolute top-2 left-2 z-10">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(listing.status)}`}>
                  {listing.status}
                </span>
              </div>
              
              {/* Edit/Delete Buttons */}
              <div className="absolute top-2 right-2 z-10 flex gap-1">
                <button 
                  className="w-6 h-6 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors duration-200 shadow-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/edit/${listing.id}`);
                  }}
                  title="Edit listing"
                >
                  <svg className="w-3 h-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button 
                  className="w-6 h-6 bg-red-500/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-red-600 transition-colors duration-200 shadow-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteListing(listing.id);
                  }}
                  title="Delete listing"
                >
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {/* Compact Listing Card */}
              <div className="compact-listing-card bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer" onClick={() => navigate(`/listing/${listing.id}`)}>
                {/* Image */}
                <div className="relative">
                  <img
                    className="w-full h-32 object-cover"
                    src={listing.photos?.[0] || '/placeholder.jpg'}
                    alt={listing.title}
                  />
                  {/* Category Badge */}
                  <div className="absolute bottom-1 left-1">
                    <span className="category-badge category-clothing text-xs">
                      {listing.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-2">
                  {/* Title */}
                  <h3 className="font-medium text-slate-900 text-xs mb-1 line-clamp-2 leading-tight">
                    {listing.title}
                  </h3>
                  
                  {/* Price and Date */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                      ${(listing.price_cents / 100).toFixed(0)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(listing.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>

                  {/* Condition */}
                  {listing.condition && (
                    <div className="mt-1 flex items-center gap-1">
                      <div className={`w-1 h-1 rounded-full ${
                        listing.condition === 'excellent' ? 'bg-green-500' :
                        listing.condition === 'good' ? 'bg-yellow-500' :
                        listing.condition === 'fair' ? 'bg-orange-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-xs text-slate-500 capitalize">{listing.condition}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
        </div>
      )}

      {activeTab === 'payments' && (
        <PaymentApproval />
      )}
    </div>
  );
}
